import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { AccountShell } from "./AccountShell";
import { LoginPage } from "@/components/layouts/LoginPage";
import { RegisterPage } from "@/components/layouts/RegisterPage";
import { ForgotPasswordPage } from "@/components/layouts/ForgotPasswordPage";
import { ResetPasswordPage } from "@/components/layouts/ResetPasswordPage";
import { GoogleCallbackPage } from "@/components/layouts/GoogleCallbackPage";
import { UserSetupPage } from "@/components/layouts/UserSetupPage";
import { AcceptInvitePage } from "@/components/layouts/AcceptInvitePage";
import { AcceptProductInvitePage } from "@/components/layouts/AcceptProductInvitePage";
import { requireAuth, requireGuest } from "@/lib/auth/route-protection";
import { returnAfterAuth } from "@/lib/auth/external-redirect";
import { getAuthCookie } from "@/lib/auth/session-cookie";
import {
  normalizeSetupSearchCode,
  parseSetupTab,
  type UserSetupSearch,
} from "@/lib/auth/user-setup-search";
import { isSignupOnboarding } from "@/lib/auth/signup-onboarding";

/**
 * Hand-authored route tree for the account (auth) app — intentionally NOT
 * file-based codegen. Keeps this app's router isolated from the pro-web
 * dashboard route tree (zero knowledge of /workspace, /fleet, etc.).
 */

function hasAccessToken(): boolean {
  return !!getAuthCookie("access_token");
}

const rootRoute = createRootRoute({
  component: AccountShell,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    if (hasAccessToken() && !isSignupOnboarding()) {
      if (returnAfterAuth()) return;
    }
    if (hasAccessToken() && isSignupOnboarding()) {
      throw redirect({ to: "/user-setup" as "/" });
    }
    throw redirect({ to: "/login" as "/" });
  },
  component: () => null,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  beforeLoad: requireGuest(),
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: () => {
    const { redirect: redirectParam } = loginRoute.useSearch();
    return <LoginPage redirect={redirectParam} />;
  },
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  beforeLoad: requireGuest(),
  validateSearch: (search: Record<string, unknown>): { email?: string; redirect?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: () => {
    const { email } = registerRoute.useSearch();
    return <RegisterPage email={email} />;
  },
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>): { email?: string; token?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: () => {
    const { email, token } = resetPasswordRoute.useSearch();
    return <ResetPasswordPage email={email} token={token} />;
  },
});

const googleCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/google/callback",
  validateSearch: (search: Record<string, unknown>) => ({
    code: (search.code as string) || undefined,
    error: (search.error as string) || undefined,
  }),
  component: () => {
    const { code, error } = googleCallbackRoute.useSearch();
    return <GoogleCallbackPage code={code} error={error} />;
  },
});

const userSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/user-setup",
  beforeLoad: requireAuth(),
  validateSearch: (search: Record<string, unknown>): UserSetupSearch => ({
    tab: parseSetupTab(search),
    step: search.step === "phone" ? "phone" : undefined,
    email: (search.email as string) || undefined,
    code: normalizeSetupSearchCode(search.code),
  }),
  component: () => {
    const search = userSetupRoute.useSearch();
    return <UserSetupPage search={search} />;
  },
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  validateSearch: (search: Record<string, unknown>) => ({
    email: (search.email as string) || undefined,
    code: normalizeSetupSearchCode(search.code),
  }),
  beforeLoad: ({ search }) => {
    throw redirect({
      to: "/user-setup" as "/",
      search: {
        email: search.email,
        code: search.code,
        tab: "email",
      },
      replace: true,
    });
  },
  component: () => null,
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invite",
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: () => {
    const { token } = acceptInviteRoute.useSearch();
    return <AcceptInvitePage token={token} />;
  },
});

const acceptProductInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-product-invite",
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: () => {
    const { token } = acceptProductInviteRoute.useSearch();
    return <AcceptProductInvitePage token={token} />;
  },
});

const healthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/health",
  component: () => <div>ok</div>,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  googleCallbackRoute,
  userSetupRoute,
  verifyEmailRoute,
  acceptInviteRoute,
  acceptProductInviteRoute,
  healthRoute,
]);

export const accountRouter = createRouter({ routeTree });

// Deliberately NOT declaring `Register.router` here — the main app's
// main.tsx already owns that global augmentation.
